import { Suspense, useContext, useEffect, useRef, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { RestAPI } from "@aws-amplify/api-rest";

import ErrorBoundary from "./components/Utils/ErrorBoundary";
import FarmProvider from "./contexts/FarmContext";
import TypesProvider from "./contexts/TranslatedTypes.js";
import withAuthenticator from "./hoc/withAuthenticator";
import { FarmContext } from "./contexts/FarmContext";
import Menubar from "./components/Menubar/Menubar";
import Header from "./components/Header/Header";
import Login from "./pages/Login/Login";
import Personal from "./pages/Personal/Personal";
import Farms from "./pages/Farms/Farms";
import Account from "./pages/Account/Account";
import PolicyAndTerms from "./pages/PolicyAndTerms/PolicyAndTerms";
import LoadingDialog from "../src/components/Utils/LoadingDialog";
import BillingMenubar from "./components/BillingMenuBar/BillingMenubar";
import { ToastContainer } from "react-toastify";
import "primeicons/primeicons.css";
import "./App.css";
import "./scss/styles.scss";
import "primereact/resources/themes/lara-light-blue/theme.css";
//import 'primeflex/primeflex.css'

import TagManager from "react-gtm-module";
import GATracker from "./components/GATracker/GATracker";
import Billing from "./pages/Billing/Billing";

import { AuthenticatorContext } from "./contexts/AuthenticatorContext";

import { RouterProvider, UNSAFE_mapRouteProperties, useLocation } from "react-router";
import { createRouter, createBrowserHistory } from "@remix-run/router";
import { putLog } from "./utils/logcache";
import BillingDocuments from "./components/BillingDocuments/BillingDocuments.js";

const TAG_MANAGER_ID = import.meta.env.VITE_GTM_CONTAINER_ID;

//ReactGA.initialize(TRACKING_ID);

const tagManagerArgs = {
    gtmId: TAG_MANAGER_ID
};
TagManager.initialize(tagManagerArgs);

/**
 *
 * @param {import("@remix-run/router").History} history
 * @param {import("@remix-run/router").AgnosticRouteObject[]} routes
 * @returns
 */
function createHistoryRouter(history, routes) {
    return createRouter({
        history, routes,
        future: { v7_prependBasename: true },
        mapRouteProperties: UNSAFE_mapRouteProperties,
    }).initialize();
}

const getRouter = () => {
    const history = createBrowserHistory({ window });
    const router = createHistoryRouter(history, [
        {
            path: "*",
            element: <Layout history={history} />
        }
    ]);

    if (!router.navigate.hack) {
        const navigate_orig = router.navigate;
        const navigate = (...params) => {
            putLog(JSON.parse(JSON.stringify(params)));
            navigate_orig(...params);
        };
        navigate.hack = true;
        router.navigate = navigate;
    }
    return router;
};
const App = () => {
    // loader functions will be called inmediatly when createBrowserRouter is called, if the route is correct
    // to prevent failing because we have no auth-data, defer the router creation to the mounting of the app component
    const [routerInfo, setRouterInfo] = useState({ router: null, idx: 0 });

    useEffect(() => {
        const router = getRouter();
        setRouterInfo((ri) => ({ router, idx: (ri.idx + 1) }));

        // we need to clear the memory of the previous router
        return () => {
            router.dispose();
        };
    }, []);

    if (!routerInfo.router) return null;
    return <RouterProvider
        key={routerInfo.idx} router={routerInfo.router}
        fallbackElement={<div />}
    />;
};

function Layout({ history }) {

    const { userInfo } = useContext(AuthenticatorContext);
    const [usuarioInfo, setInfo] = useState({ data: [], isLoaded: false });
    const [personalInfo, setPersonalInfo] = useState({ data: [], isLoaded: false });
    const selectorRef = useRef(null);
    const mapRef = useRef(null);
    const [dataSource, setDataSource] = useState({ loading: false, data: null });
    const [fields, setFields] = useState(null);
    const [menuIsOpen, setMenuIsOpen] = useState(null);
    const historyRef = useRef(null);
    historyRef.current = history;
    useEffect(() => {
        RestAPI.get("apiAdminDropControl", "/personal")
            .then(res => {
                const json = JSON.parse(JSON.stringify(res));
                setInfo({ isLoaded: true, data: json });
            })
            .catch(err => console.error(err));
    }, []);

    useEffect(() => {
        const newPersonalInfo = { ...usuarioInfo.data };

        if (userInfo && newPersonalInfo) {
            if (userInfo.name) {
                newPersonalInfo.nombre = userInfo.name;
            }
            if (userInfo.family_name
        && userInfo.family_name !== `${newPersonalInfo.apellido_paterno || ""} ${newPersonalInfo.apellido_materno || ""}`) {
                const idx = userInfo.family_name.indexOf(" ");
                const splits = idx > -1 ? [userInfo.family_name.slice(0, idx), userInfo.family_name.slice(idx + 1)]
                    : [userInfo.family_name, ""];
                newPersonalInfo.apellido_paterno = splits[0] || "";
                newPersonalInfo.apellido_materno = splits[1] || "";
            }
        }

        setPersonalInfo({ isLoaded: usuarioInfo.isLoaded, data: newPersonalInfo });
    }, [usuarioInfo, userInfo]);

    return <ErrorBoundary name="Root">
        <ToastContainer icon={false} theme="colored" />
        <Suspense fallback={<div />}>
            <div className="control-section">
                <TypesProvider>
                    <FarmProvider>
                        <FarmComponent
                            usuarioInfo={usuarioInfo}
                            personalInfo={personalInfo}
                            historyRef={historyRef}
                            selectorRef={selectorRef}
                            mapRef={mapRef}
                            fields={fields}
                            setFields={setFields}
                            dataSource={dataSource}
                            setDataSource={setDataSource}
                            menuIsOpen={menuIsOpen}
                            setMenuIsOpen={setMenuIsOpen}
                            setInfo={setInfo}
                        />
                    </FarmProvider>
                </TypesProvider>
            </div>
        </Suspense>
    </ErrorBoundary>;
}

const FarmComponent = ({ usuarioInfo, personalInfo,
    historyRef, selectorRef, mapRef, fields, setFields, dataSource, setDataSource,
    menuIsOpen, setMenuIsOpen, setInfo }) => {
    const { refreshUser, signOut, userInfo } = useContext(AuthenticatorContext);
    const [ds] = useContext(FarmContext);

    return (
        <div>
            <GATracker userInfo={userInfo} dataFarm={ds?.dataFarm ?? null} history={historyRef.current} />
            {!usuarioInfo.isLoaded &&
        <LoadingDialog />}
            {usuarioInfo.data.politicas_aceptadas && usuarioInfo.isLoaded &&
        <div id="wrapper" className={(ds?.dataFarm?.acceso_limitado ? "acceso_limitado " : "") + (!menuIsOpen ? "sidebar-closed" : "sidebar-open")}>
            <Header dataUser={personalInfo.data} userInfo={userInfo} signOut={signOut}
                toggleSidebar={() => setMenuIsOpen(c => !c)}
            />
            <Routes>
                <Route path="/billing/*" element={
                    <BillingMenubar setMenuIsOpen={setMenuIsOpen} />
                }
                />
                <Route path="*" element={
                    <Menubar historyRef={historyRef} innerRef={selectorRef} mapRef={mapRef} userInfo={userInfo}
                        fields={fields} setFields={setFields} dataSource={dataSource} setDataSource={setDataSource} menuIsOpen={menuIsOpen} setMenuIsOpen={setMenuIsOpen}
                    />
                }
                />
            </Routes>
            <div id="sidebar-content">
                <ErrorBoundary name="Route" resetOnChange>
                    <Routes>
                        <Route path="/personal" element={
                            <Personal info={personalInfo} refreshUser={refreshUser} setInfo={setInfo} />
                        }
                        />
                        <Route path="/account" element={
                            <Account mapRef={mapRef} fields={fields} />
                        }
                        />
                        <Route path="/farms/*" element={
                            <Farms mapRef={mapRef} fields={fields} selectorRef={selectorRef} accountsData={dataSource.data} usuarioInfo={usuarioInfo.data} menuIsOpen={menuIsOpen} />
                        }
                        />
                        {
                            personalInfo?.data?.comercial?
                                <Route path="/billing/*" element={
                                    <Billing  info={personalInfo} refreshUser={refreshUser} setInfo={setInfo} />
                                }
                                />
                                :<Route path="/billing/documents/:hub/:date" element={<BillingDocuments />} />
                        }
                    </Routes>
                </ErrorBoundary>
            </div>
        </div>}
            {!usuarioInfo.data.politicas_aceptadas && usuarioInfo.isLoaded &&
        <PolicyAndTerms email={usuarioInfo.data.email} />}
        </div>
    );
};

export default withAuthenticator(App, Login, LoadingDialog);
